import React, { useState, useEffect, useContext } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, TextField } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import Modal from 'components/_layouts/Modal';
import { FilterAlt } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { GetDTBPackageParams } from 'views/App/Portal/DTB/_types/dtb';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import ManualPicker from 'components/_pickers/ManualPicker';
import { AppContext } from 'views/App/context';

export type DTBPackageFiltersProps = {
  params: GetDTBPackageParams;
  updateParams: (updatedParams: GetDTBPackageParams) => void;
};

const DTBPackageFilters: React.FC<DTBPackageFiltersProps> = ({ params, updateParams }) => {
  const { currentOrganizationId, isLexipolStaff } = useContext(AppContext);
  const [showFilters, setShowFilters] = useState(false);
  const [jurisdiction, setJurisdiction] = useState<string>(params.jurisdictionId || '');
  const [manualId, setManualId] = useState<string>(params.manualId || '');
  const [status, setStatus] = useState<string>(params.status || 'ALL');
  const [limit, setLimit] = useState<string>(params.limit || '');
  const [createdDateRange, setCreatedDateRange] = useState<DateRange<Dayjs>>([
    params.createdAtStart ? dayjs(params.createdAtStart) : null,
    params.createdAtEnd ? dayjs(params.createdAtEnd) : null,
  ]);

  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: t('common.dateRangeFilters.lastMonth'),
      getValue: () => {
        const today = dayjs();
        const startOfLastMonth = today.startOf('month').subtract(1, 'month');
        return [startOfLastMonth, startOfLastMonth.endOf('month')];
      },
    },
    {
      label: t('common.dateRangeFilters.lastQuarter'),
      getValue: () => {
        const today = dayjs();
        const startOfLastQuarter = today.startOf('quarter').subtract(1, 'quarter');
        return [startOfLastQuarter, startOfLastQuarter.endOf('quarter')];
      },
    },
    {
      label: t('common.dateRangeFilters.thisYear'),
      getValue: () => {
        const today = dayjs();
        const startOfYear = today.startOf('year');
        return [startOfYear, today];
      },
    },
    {
      label: t('common.dateRangeFilters.lastYear'),
      getValue: () => {
        const today = dayjs();
        const startOfLastYear = today.startOf('year').subtract(1, 'year');
        return [startOfLastYear, startOfLastYear.endOf('year')];
      },
    },
  ];

  useEffect(() => {
    setJurisdiction(params.jurisdictionId || '');
    setManualId(params.manualId || '');
    setStatus(params.status || 'ALL');
    setLimit(params.limit || '');
    setCreatedDateRange([
      params.createdAtStart ? dayjs(params.createdAtStart) : null,
      params.createdAtEnd ? dayjs(params.createdAtEnd) : null,
    ]);
  }, [params]);

  const handleApplyFilters = () => {
    const newParams: GetDTBPackageParams = {
      orgId: params.orgId,
      jurisdictionId: jurisdiction || undefined,
      manualId: manualId || undefined,
      status: status || undefined,
      limit: limit || undefined,
    };

    if (createdDateRange && createdDateRange[0] && createdDateRange[1]) {
      if (createdDateRange[0].isAfter(createdDateRange[1])) {
        return;
      }
      newParams.createdAtStart = createdDateRange[0].format('YYYY-MM-DD');
      newParams.createdAtEnd = createdDateRange[1].format('YYYY-MM-DD');
    }
    updateParams(newParams);
    setShowFilters(false);
  };

  return (
    <>
      <Button
        onClick={() => setShowFilters(true)}
        size='small'
        color='neutral'
        startIcon={<FilterAlt />}
        className='icon-only'
      />
      <Modal
        title={t('dtbs.packages.filters.root')}
        size='sm'
        actions={
          <>
            <Button
              onClick={() => setShowFilters(false)}
              color='neutral'
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleApplyFilters}
            >
              {t('dtbs.packages.filters.apply')}
            </Button>
          </>
        }
        open={showFilters}
        onClose={() => setShowFilters(false)}
      >
        <Stack spacing={4}>
          <FormControl>
            <FormLabel>{t('dtbs.packages.filters.status')}</FormLabel>
            <RadioGroup
              row
              value={status}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStatus(e.target.value)}
            >
              <FormControlLabel
                value='ALL'
                control={<Radio />}
                label='All'
              />
              <FormControlLabel
                value='DRAFT'
                control={<Radio />}
                label='Draft'
              />
              <FormControlLabel
                value='APPROVED'
                control={<Radio />}
                label='Approved'
              />
              <FormControlLabel
                value='DISTRIBUTED'
                control={<Radio />}
                label='Distributed'
              />
            </RadioGroup>
          </FormControl>
          {isLexipolStaff && (
            <JurisdictionPicker
              id='jurisdiction-picker'
              label={t('dtbs.packages.filters.jurisdiction')}
              value={jurisdiction ? [jurisdiction] : []}
              onChange={(val: string[]) => setJurisdiction(val[0] || '')}
              placeholder={t('dtbs.packages.filters.jurisdiction')}
            />
          )}
          <ManualPicker
            id='manual-picker'
            label={t('dtbs.library.filters.manualName')}
            organizationId={currentOrganizationId || ''}
            value={manualId ? [manualId] : []}
            onChange={(newValue: string[]) => setManualId(newValue[0] || '')}
            placeholder={t('dtbs.library.filters.selectManual')}
          />

          <FormControl fullWidth>
            <FormLabel>{t('dtbs.packages.filters.createdDate')}</FormLabel>
            <DateRangePicker
              value={createdDateRange}
              onChange={(newValue) => setCreatedDateRange(newValue)}
              slotProps={{
                shortcuts: {
                  items: shortcutsItems,
                },
                actionBar: { actions: [] },
              }}
              sx={{
                '& .MuiFormControl-root': { paddingTop: 0 },
                '& .MuiFormLabel-root': { display: 'none' },
              }}
            />
          </FormControl>
        </Stack>
      </Modal>
    </>
  );
};

export default DTBPackageFilters;
